"use client";
// ===================================================
// GitCockpit — live, actionable pipeline state for the current branch.
// ===================================================
// Sits at the top of DevStatusBoard. Unlike the board below it (which reads the
// 15-min-stale dev_status_items snapshot), this reads LIVE git state of the
// serving host via /api/git/status and lets you advance it in one click:
//   🔴 לא שמור (dirty) → Commit → 🟡 Committed (ahead>0) → שמור→GitHub → 🟢 on GitHub
// Self-hides where git actions aren't allowed (real production → 403). The whole
// point: make "not yet on GitHub" visible + one-click fixable, so work stops
// getting lost on disk. Reuses src/lib/git-actions (Rule 10). Vercel/deploy is
// intentionally NOT here — that stays gated (Rule 25).
import { useCallback, useEffect, useState } from "react";
import { GitCommit, ArrowUpCircle, Loader2, RefreshCw } from "lucide-react";
import { gitStatus, gitCommit, gitDeploy, type GitStatus } from "@/lib/git-actions";

type Stage = "unsaved" | "committed" | "github";

function deriveStage(s: GitStatus): Stage {
  if (s.isDirty) return "unsaved";
  if (s.ahead > 0) return "committed";
  return "github";
}

const STAGE_UI: Record<Stage, { dot: string; label: string; tone: string }> = {
  unsaved:   { dot: "bg-red-400",     label: "לא שמור",   tone: "border-red-800 bg-red-950/40 text-red-200" },
  committed: { dot: "bg-yellow-400",  label: "Committed", tone: "border-yellow-800/60 bg-yellow-950/30 text-yellow-200" },
  github:    { dot: "bg-emerald-400", label: "שמור ב-GitHub", tone: "border-emerald-800/60 bg-emerald-950/30 text-emerald-200" },
};

function narrate(stage: Stage, s: GitStatus): string {
  const n = s.modified.length + s.untracked.length;
  if (stage === "unsaved") return `אתה על ${s.branch} · ${n} שינויים לא שמורים · לחץ Commit כדי לשמור`;
  if (stage === "committed") return `${s.ahead} commits עדיין לא ב-GitHub · לחץ שמור→GitHub כדי לגבות`;
  return `הכל שמור ב-GitHub ✅ · ${s.branch}`;
}

export function GitCockpit() {
  const [status, setStatus] = useState<GitStatus | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null); // null=loading · false=hidden (prod)
  const [busy, setBusy] = useState<null | "commit" | "push" | "refresh">(null);
  const [msg, setMsg] = useState("");
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);

  const refresh = useCallback(async () => {
    const res = await gitStatus();
    if (!res.ok) { setAvailable(false); return; }
    setAvailable(true);
    setStatus(res.status);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (available === false) return null; // not a git-action environment → render nothing
  if (available === null || !status) {
    return <div className="rounded-xl border border-slate-700 bg-slate-800/30 p-3 text-xs text-slate-500" dir="rtl">טוען מצב git…</div>;
  }

  const stage = deriveStage(status);
  const ui = STAGE_UI[stage];
  const canCommit = status.mutationsEnabled && busy === null && status.isDirty;
  const canPush = status.mutationsEnabled && busy === null && (status.isDirty || status.ahead > 0);

  const doCommit = async () => {
    setBusy("commit"); setFeedback(null);
    const r = await gitCommit(msg.trim() || `wip: ${status.branch}`);
    setFeedback(r.success ? { ok: true, text: `נשמר מקומית · ${r.hash ?? ""}` } : { ok: false, text: r.error || "שגיאה ב-Commit" });
    if (r.success) setMsg("");
    setBusy(null); refresh();
  };
  const doPush = async () => {
    setBusy("push"); setFeedback(null);
    const r = await gitDeploy(msg.trim() || undefined);
    setFeedback(r.success ? { ok: true, text: `נדחף ל-GitHub ✅ · ${r.commitHash ?? ""}` } : { ok: false, text: r.error || "שגיאה ב-Push" });
    if (r.success) setMsg("");
    setBusy(null); refresh();
  };

  return (
    <div dir="rtl" className={`rounded-xl border p-3 space-y-2 ${ui.tone}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${ui.dot} ${stage === "unsaved" ? "animate-pulse" : ""}`} />
          <span className="text-sm font-semibold">{ui.label}</span>
          <span className="font-mono text-[11px] opacity-70">{status.branch}</span>
        </div>
        <button type="button" onClick={() => { setBusy("refresh"); refresh().finally(() => setBusy(null)); }}
          title="רענן מצב" className="opacity-60 transition-opacity hover:opacity-100">
          <RefreshCw size={13} className={busy === "refresh" ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="text-xs opacity-90">{narrate(stage, status)}</div>

      <div className="flex items-center gap-2">
        <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="תיאור (לא חובה)"
          dir="rtl" className="min-w-0 flex-1 rounded-md border border-white/10 bg-black/20 px-2 py-1 text-xs text-slate-100 outline-none placeholder:text-slate-500 focus:border-white/25"
          onKeyDown={(e) => { if (e.key === "Enter" && canPush) doPush(); }} />
        <button type="button" onClick={doCommit} disabled={!canCommit}
          className="flex shrink-0 items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs text-slate-200 transition-colors hover:bg-white/10 disabled:opacity-40">
          {busy === "commit" ? <Loader2 size={12} className="animate-spin" /> : <GitCommit size={12} />} Commit
        </button>
        <button type="button" onClick={doPush} disabled={!canPush}
          className="flex shrink-0 items-center gap-1 rounded-md border border-teal-500/40 bg-teal-500/15 px-2 py-1 text-xs text-teal-100 transition-colors hover:bg-teal-500/25 disabled:opacity-40">
          {busy === "push" ? <Loader2 size={12} className="animate-spin" /> : <ArrowUpCircle size={12} />} שמור→GitHub
        </button>
      </div>

      {feedback && (
        <div className={`text-[11px] ${feedback.ok ? "text-emerald-300" : "text-red-300"}`}>{feedback.text}</div>
      )}
    </div>
  );
}
